import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import webPushService from '../utils/webPush';
import axios from 'axios';
import Api from '../api/userApi';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsFetched, setNotificationsFetched] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const socketRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const { user } = useAuth();

  // Fetch notifications only once when user is available
  useEffect(() => {
    if (user && !notificationsFetched) {
      const fetchNotifications = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${Api.notifications}?limit=10`, {
            timeout: 10000, // 10 second timeout
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined
            }
          });
          setNotifications(response.data.notifications || []);
          setUnreadCount(response.data.unreadCount || 0);
          setNotificationsFetched(true);
        } catch (error) {
          // Silently handle network errors to prevent console spam
          // Network errors are common when server is unreachable and should not be logged
          const isNetworkError = 
            error.code === 'ERR_INSUFFICIENT_RESOURCES' || 
            error.code === 'ERR_NETWORK' || 
            error.code === 'ECONNABORTED' ||
            error.message === 'Network Error' ||
            (error.request && !error.response); // Request made but no response (network issue)
          
          // Only log non-network errors (like 401, 403, 500, etc.)
          // Suppress all network-related errors to prevent console spam
          if (!isNetworkError && error.response) {
            // Only log HTTP errors (4xx, 5xx) that have a response
            console.warn('Notifications API error:', error.response.status, error.response.data?.message || error.message);
          }
          // Mark as fetched to prevent retries, even on error
          setNotificationsFetched(true);
        }
      };
      fetchNotifications();
    }
  }, [user, notificationsFetched]);

  useEffect(() => {
    if (user) {
      // Initialize web push notifications and (re)bind this browser's push
      // subscription to the current user. This runs whenever the logged-in user
      // changes, so each user on a shared browser gets their own subscription.
      console.log('[Socket] Active user changed — initializing push for user:', (user._id || user.id)?.toString?.());
      webPushService.initialize();

      // Initialize socket connection - use the same server as API
      const envBase = process.env.REACT_APP_API_BASE_URL;
      const localWebServer = 'http://localhost:5001';
      
      // Use environment variable if set, otherwise detect based on current origin
      let server = envBase;
      if (!server) {
        const currentOrigin = window.location.origin;
        if (currentOrigin === 'https://www.ulcaapp.com') {
          // Production frontend - use production API
          server = 'https://www.ulcaapp.com';
        } else if (currentOrigin === 'https://ulcaapp.testatozas.in') {
          // Test/staging frontend - use test API
          server = 'https://ulcaapp.testatozas.in';
        } else {
          // Development - use localhost
          server = localWebServer;
        }
      }
      const socketUrl = process.env.REACT_APP_SERVER_URL || server;
      
      const newSocket = io(socketUrl, {
        auth: {
          token: localStorage.getItem('token')
        },
        reconnection: true,
        reconnectionDelay: 2000, // Increased delay
        reconnectionAttempts: 3, // Reduced attempts to prevent resource exhaustion
        reconnectionDelayMax: 5000,
        timeout: 20000,
        transports: ['polling', 'websocket'], // Try polling first, then websocket
        forceNew: false // Reuse connection if available
      });

      // Join user's room for notifications
      // Use user._id if available, otherwise use user.id
      const userId = user._id || user.id;
      if (userId) {
        newSocket.emit('join-room', userId.toString());
        console.log('Joined notification room for user:', userId.toString());
      }

      // Handler for notifications
      const handleNotification = (notification) => {
        // Stable de-dupe (Socket.IO + SW push bridge) using an in-memory Set.
        // Key preference: id || _id || data.notificationId || tag.
        const keyRaw =
          notification?.id ||
          notification?._id ||
          notification?.data?.notificationId ||
          notification?.tag ||
          null;
        const key =
          keyRaw !== null && keyRaw !== undefined && `${keyRaw}`.trim()
            ? `${keyRaw}`.trim()
            : `${notification?.type || 'notification'}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const seen = seenIdsRef.current;
        if (seen.has(key)) {
          // Still show toast (so user gets feedback), but don't duplicate state/badge.
          // NOTE: toast is below; we return early after showing toast.
        } else {
          seen.add(key);
          // Keep only last 50 keys (in insertion order).
          while (seen.size > 50) {
            const oldest = seen.values().next().value;
            if (oldest === undefined) break;
            seen.delete(oldest);
          }

          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((c) => c + 1);
        }
        
        // Show toast notification
        toast.info(notification.title, {
          onClick: () => {
            // Handle notification click - navigate to relevant page
            const deepUrl =
              typeof notification.data?.url === 'string' && notification.data.url.trim()
                ? notification.data.url.trim()
                : '';
            if (deepUrl.startsWith('/')) {
              window.location.href = deepUrl;
            } else if (notification.data?.bookingId) {
              window.location.href = `/bookings/${notification.data.bookingId}`;
            } else if (notification.data?.jobRequestId) {
              window.location.href = `/job-requests/${notification.data.jobRequestId}`;
            } else if (notification.type === 'no_workers_found' && notification.data?.jobRequestId) {
              window.location.href = `/my-job-requests?jobRequestId=${notification.data.jobRequestId}`;
            }
          }
        });

        // Show OS-level notification only when the app is NOT in the foreground.
        // This prevents "push-like" notifications from firing just because the user opened the site.
        const isForeground =
          document.visibilityState === 'visible' &&
          typeof document.hasFocus === 'function' &&
          document.hasFocus();

        // If this came from a SW push message, the Service Worker is already responsible for
        // the system notification. Avoid duplicating OS-level notifications from the page.
        const isFromPushBridge = notification?.data?.__source === 'push';

        // If push notifications are active, the Service Worker handles all OS-level
        // notifications (the "www.ulcaapp.com" style).  Showing one from the page too
        // would cause a duplicate ("via Microsoft Edge" style) because browser
        // notifications from the page context and the SW context are separate — tag
        // de-duplication does NOT work across contexts.
        const hasPushSubscription = !!webPushService.pushSubscription;

        if (!isForeground && !isFromPushBridge && !hasPushSubscription) {
          const requireInteractionTypes = new Set([
            'booking_request',
            'booking_accepted',
            'booking_completed',
            'booking_expired',
            'booking_schedule_update',
            'booking_schedule_update_accepted',
            'booking_schedule_update_rejected',
            'work_before_photos_reminder',
            'booking_completion_reminder',
            'booking_extended',
            'work_after_photos_reminder',
            'job_application',
            'job_application_needs_confirmation',
            'application_accepted',
            'application_rejected',
            'job_accepted',
            'job_expired',
            'no_workers_found',
            'payment_received'
          ]);

          const notifId =
            notification?.id ||
            notification?._id ||
            notification?.data?.notificationId ||
            Date.now();

          const tag = `${notification.type}-${notifId}`;

          const completionActions =
            notification.type === 'booking_completion_reminder'
              ? [
                  { action: 'complete', title: 'Yes, Completed' },
                  { action: 'extend', title: 'Extend Work' }
                ]
              : (notification.type === 'booking_request' ||
                 notification.type === 'no_workers_found')
                ? [
                    { action: 'view', title: 'View' },
                    { action: 'dismiss', title: 'Dismiss' }
                  ]
                : [];

          webPushService.showNotification(notification.title, {
            body: notification.data?.pushBody || notification.message,
            tag,
            data: {
              type: notification.type,
              bookingId: notification.data?.bookingId,
              jobRequestId: notification.data?.jobRequestId,
              ...notification.data
            },
            requireInteraction: requireInteractionTypes.has(notification.type),
            actions: completionActions
          });
        }
      };

      // Handler for new messages
      const handleNewMessage = (messageData) => {
        toast.info(`New message from ${messageData.sender.name}`, {
          onClick: () => {
            // Navigate to booking details
            window.location.href = `/bookings/${messageData.bookingId}`;
          }
        });
      };

      // Handler for connection
      const handleConnect = () => {
        console.log('Connected to server');
        // Re-validate/re-bind this device's push subscription on every (re)connect.
        // For a passive Worker who mostly keeps the app closed, this maximizes the
        // chance the server holds a live endpoint when later pushes are sent
        // (e.g. "work completed" / "payment"). Throttled inside the service.
        try {
          webPushService.refreshSubscription();
        } catch (e) {
          // best-effort; never block socket setup
        }
      };

      // Handler for disconnection
      const handleDisconnect = () => {
        console.log('Disconnected from server');
      };

      // Handler for connection errors
      const handleConnectError = (error) => {
        // Suppress excessive error logging to prevent console spam
        if (error.message && 
            !error.message.includes('ERR_CONNECTION_REFUSED') && 
            !error.message.includes('xhr poll error') &&
            !error.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
          console.error('Socket connection error:', error);
        }
        // Don't log common network errors that will retry automatically
      };

      // Handler for global userDeleted events (emitted by admin actions)
      // This is used to invalidate any cached bookings/availability in
      // schedule-related components so that freed time slots become immediately
      // bookable without a full page refresh.
      const handleUserDeleted = (payload) => {
        try {
          console.log('Received userDeleted event from server:', payload);
        } catch (e) {
          // Ignore console issues in restricted environments
        }
        // Bump a simple version counter; any subscriber that depends on
        // scheduleVersion can use this to trigger a refetch/recompute.
        setScheduleVersion((prev) => prev + 1);
      };

      // Register event listeners
      newSocket.on('notification', handleNotification);
      newSocket.on('new-message', handleNewMessage);
      newSocket.on('connect', handleConnect);
      newSocket.on('disconnect', handleDisconnect);
      newSocket.on('connect_error', handleConnectError);
      newSocket.on('userDeleted', handleUserDeleted);

      // Also listen for custom events dispatched by the WebPushService (SW -> window event bridge).
      const onPushEvent = (evt) => {
        try {
          const p = evt?.detail || {};
          const data = (p.data && typeof p.data === 'object' && !Array.isArray(p.data)) ? p.data : {};

          const mapped = {
            id: data.notificationId || undefined,
            type: data.type || 'push',
            title: p.title || 'New Notification',
            message: p.body || 'You have a new notification',
            data: {
              ...data,
              __source: 'push'
            },
            createdAt: new Date().toISOString(),
            isRead: false
          };

          handleNotification(mapped);
        } catch (e) {
          // ignore
        }
      };

      window.addEventListener('ulcaapp:push', onPushEvent);

      // Refresh the push subscription whenever the app returns to the foreground.
      // On mobile, the OS can kill the browser while it's in the background, which
      // silently invalidates the push endpoint. Different platforms surface
      // "resume" via different events, so we listen for all of them to reliably
      // re-sync a live endpoint with the server:
      //   • visibilitychange → visible: tab/app switching (all browsers)
      //   • window focus: desktop window/tab refocus
      //   • pageshow: back/forward (bfcache) restore, common on mobile Safari/PWA
      // refreshSubscription() is throttled internally (60s), so registering
      // several triggers never causes excessive network calls.
      const refreshOnForeground = () => {
        try {
          webPushService.refreshSubscription();
        } catch (e) {
          // best-effort; never block the app
        }
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          refreshOnForeground();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', refreshOnForeground);
      window.addEventListener('pageshow', refreshOnForeground);

      socketRef.current = newSocket;
      setSocket(newSocket);

      // Cleanup on unmount - remove all event listeners and close socket
      return () => {
        if (socketRef.current) {
          // Remove all event listeners to prevent duplicate handlers
          socketRef.current.off('notification', handleNotification);
          socketRef.current.off('new-message', handleNewMessage);
          socketRef.current.off('connect', handleConnect);
          socketRef.current.off('disconnect', handleDisconnect);
          socketRef.current.off('connect_error', handleConnectError);
          socketRef.current.off('userDeleted', handleUserDeleted);
          
          if (socketRef.current.connected) {
            socketRef.current.close();
          }
          socketRef.current = null;
        }

        window.removeEventListener('ulcaapp:push', onPushEvent);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', refreshOnForeground);
        window.removeEventListener('pageshow', refreshOnForeground);
      };
    } else {
      // Disconnect socket if user logs out
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setSocket(null);
      setNotifications([]);
      setUnreadCount(0);
      setNotificationsFetched(false); // Reset for next login
    }
  }, [user]); // socket is managed via ref to prevent infinite loops

  const markNotificationAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => {
        const id = notif.id || notif._id;
        if (id === notificationId) {
          return { ...notif, isRead: true };
        }
        return notif;
      })
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => (notif.isRead ? notif : { ...notif, isRead: true }))
    );
    setUnreadCount(0);
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const value = {
    socket,
    notifications,
    unreadCount,
    scheduleVersion,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearAllNotifications
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};